import React, { useMemo, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useElements } from '@stripe/react-stripe-js';
import { GlobalContext } from 'context/GlobalProvider';
import { formatPrice } from 'library/helpers/utils';
import Checkbox from 'components/UI/Antd/Checkbox/Checkbox';
import Row from 'components/UI/Antd/Grid/Row';
import Col from 'components/UI/Antd/Grid/Col';
import { sanitizeTranslation } from 'library/helpers/translationHelpers';

import {
  CardElementWrapper,
  Footer,
  FieldDescription,
  FormField,
  Divider,
  CardInput,
  TermsOfService,
  PaidWrapper,
  FieldTitle,
  FieldContent,
  FieldWrapper,
  PaidTopSection,
  CardDetailsWrapper,
  ErrorMessage
} from './CardForm.style';

import freeCancllationIcon from 'assets/images/payments/free-cancellation.svg';
import {formatLocalizedDate} from "../../../../library/helpers/dates";

const useOptions = () => {
  const options = useMemo(
    () => ({
      style: {
        base: {
          fontFamily: 'Lato',
          fontSize: '15px',
          fontWeight: 'normal',
          fontStretch: 'normal',
          fontStyle: 'normal',
          lineHeight: 'normal',
          letterSpacing: 'normal',
          color: '#8d8d8d',
          '::placeholder': {
            fontFamily: 'Lato',
            fontSize: '15px',
            fontWeight: 'normal',
            fontStretch: 'normal',
            fontStyle: 'normal',
            lineHeight: 'normal',
            letterSpacing: 'normal',
            color: '#8d8d8d'
          }
        },
        invalid: {
          color: '#9e2146'
        }
      }
    }),
    []
  );

  return options;
};

const marinaLogos = (marina_id) => {
  return ((process.env.REACT_APP_FIREBASE_ENV === 'PRODUCTION' && marina_id === 82) 
  || (process.env.REACT_APP_FIREBASE_ENV === 'TEST' && marina_id === 411))
}

const CardForm = ({
  paid,
  reservation,
  currency,
  enablePaymentButton,
  setEnablePaymentButton,
  showCardError,
  setShowCardError,
  error,
  setError,
  CardElement,
  marina_id
}) => {
  const { t } = useTranslation();
  const elements = useElements();
  const options = useOptions();

  const marinaAlipa = ((process.env.REACT_APP_FIREBASE_ENV === "PRODUCTION" && marina_id === 124 ) || (process.env.REACT_APP_FIREBASE_ENV === "TEST" && marina_id === 414 ));
  const marinaPuertoSherry = ((process.env.REACT_APP_FIREBASE_ENV === "PRODUCTION" && marina_id === 98 ) || (process.env.REACT_APP_FIREBASE_ENV === "TEST" && marina_id === 352 ));
  const marinaLiverpool = ((process.env.REACT_APP_FIREBASE_ENV === "PRODUCTION" && marina_id === 22 ) || (process.env.REACT_APP_FIREBASE_ENV === "TEST" && marina_id === 265 ));

  const { termsOfServiceLink } = useContext(GlobalContext);

  const { start_date } = reservation;

  useEffect(() => {
    if (elements) elements.getElement(CardElement).focus();
  }, [showCardError]);

  const getFormattedPaidAtDate = () => {
    const { payment } = reservation;
    if (payment && payment.paid_at) {
      return moment(payment.paid_at).format('MMMM Do YYYY');
    }
  };

  const getHiddenCardNumber = () => {
    return <span>'********'</span>;
  };

  const renderCardType = () => {
    const { payment } = reservation;
    // TODO:: Should render an image here of the specific card type.
    if (payment && payment.card_type) {
      return <span>payment.card_type</span>;
    }
    return null;
  };

  const onChange = event => {
    if (event.empty) {
      setShowCardError(false);
    }

    if (event.error) {
      setShowCardError(true);
      setError(event.error);
    } else {
      setShowCardError(false);
      setError(null);
    }

    if (event.complete) {
      setEnablePaymentButton(true);
      setShowCardError(false);
    } else {
      setEnablePaymentButton(false);
    }
  };

  if (paid) {
    return (
      <PaidWrapper>
        <PaidTopSection>
          <Row gutter={30}>
            <Col sm={24} lg={12}>
              <FieldWrapper>
                <FieldTitle>
                  {t('RESERVATION_APPROVED_DETAILS_PAYMENT_METHOD')}
                </FieldTitle>
                <FieldContent className='card-details'>
                  <CardDetailsWrapper>
                    {renderCardType()}
                    {getHiddenCardNumber()}
                  </CardDetailsWrapper>
                </FieldContent>
              </FieldWrapper>
            </Col>
            <Col sm={24} lg={12}>
              <FieldWrapper>
                <FieldTitle>
                  {t('RESERVATION_APPROVED_DETAILS_ORDER_PLACED')}
                </FieldTitle>
                <FieldContent className='order-paid-at'>
                  {getFormattedPaidAtDate()}
                </FieldContent>
              </FieldWrapper>
            </Col>
          </Row>
        </PaidTopSection>
      </PaidWrapper>
    );
  } else {
    return (
      <form>
        <FormField>
          <CardInput>
            <CardElementWrapper
              className={showCardError && 'card-error-styles'}
            >
              <CardElement
                options={options}
                onChange={event => onChange(event)}
              />
            </CardElementWrapper>
            {error && <ErrorMessage>{error.message}</ErrorMessage>}
            <Checkbox defaultChecked={false} onChange={() => {}}>
              {t('RESERVATION_PAYMENT_INFO_CREDIT_CARD_SAVE_CARD')}
            </Checkbox>
          </CardInput>
        </FormField>
        <Divider />
        <Footer>
          <TermsOfService>
            <p>
              {t('RESERVATION_TERMS_OF SERVICE_BUTTON')}{' '}
              <a href={termsOfServiceLink || '#'} target='_blank'>
                {t('TERMS_OF SERVICE_BUTTON')}
              </a>
            </p>
          </TermsOfService>
        </Footer>
      </form>
    );
  }
};

export default CardForm;
